import { Component, inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArtistService } from '../services/artist.service';
import { FrameOwnershipService } from '../services/frame-ownership.service';
import { FrameImagePreviewComponent } from '../shared/frame-image-preview/frame-image-preview.component';
import {
  ArtistLite,
  FRAME_OWNERSHIP_MESSAGES,
  FrameCatalogEntry,
  FrameOwnership,
  PickerRow,
  filterPickerFrames,
  framesOwnedBy,
  isAssignableArtist,
  needsTakeConfirm,
  scopeLabel
} from '../utils/frame-ownership.util';

const PICKER_PAGE_SIZE = 50;

@Component({
  selector: 'app-artist-management',
  standalone: true,
  imports: [CommonModule, FormsModule, FrameImagePreviewComponent],
  templateUrl: './artist-management.component.html',
  styleUrls: ['./artist-management.component.css']
})
export class ArtistManagementComponent implements OnInit, OnDestroy {
  private artistService = inject(ArtistService);
  private frameOwnership = inject(FrameOwnershipService);
  artists$: Observable<any[]>;
  artists: any[] = [];

  // KAN-149: frame ownership (Assigned frames list + all-frames picker)
  readonly ownershipText = FRAME_OWNERSHIP_MESSAGES;
  readonly scopeLabel = scopeLabel;
  private artistsById = new Map<string, ArtistLite>(); // includes deleted artists (owner badges)
  frameCatalog: FrameCatalogEntry[] = [];
  ownerships = new Map<string, FrameOwnership>();
  assignedFrames: FrameCatalogEntry[] = [];
  framesLoading = false;
  framesLoaded = false;
  framesError: string | null = null;
  rowBusy = new Set<string>();
  rowError = new Map<string, string>();
  removeConfirmFrameId: string | null = null;
  thumbUrls = new Map<string, string | null>();
  previewUrl: string | null = null;

  isPickerOpen = false;
  pickerShowAll = false;
  pickerSearch = '';
  pickerScope: 'all' | 'global' | 'device' = 'all';
  pickerPage = 0;
  pickerRows: PickerRow[] = [];
  pickerSelected = new Set<string>();
  pickerConfirming = false;
  pickerSaving = false;
  pickerMessage: string | null = null;
  pickerError: string | null = null;
  
  // Artist Form
  newArtist: any = { name: '', email: '', phone: '', address: '', commission_amount: null, bio: '', isActive: true };
  editingId: string | null = null;

  // Modal States
  isModalOpen = false;
  isSaving = false;
  errorMessage: string | null = null;
  generalErrorMessage: string | null = null;

  // File Upload State
  uploadingFile = false;
  uploadProgress = 0;
  uploadedFileUrl: string | null = null;
  deletingFile: any = null;

  // Sorting
  sortColumn = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Subscriptions
  private allFilesSub: Subscription | null = null;
  private artistsSub: Subscription | null = null;

  constructor() {
    this.artists$ = this.artistService.getArtists().pipe(
      map(data => {
        this.artistsById = new Map(data.map(a => [a.id, { id: a.id, name: a.name, isActive: a.isActive, isDeleted: a.isDeleted } as ArtistLite]));
        const artists = data.filter(a => !a.isDeleted).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        this.artists = artists;
        this.sortArtists();
        return artists;
      })
    );
  }

  ngOnInit() {
    this.artistsSub = this.artists$.subscribe();
  }

  ngOnDestroy() {
    this.allFilesSub?.unsubscribe();
    this.artistsSub?.unsubscribe();
  }

  // --- Artist CRUD ---

  openAddModal() {
    this.cancelEdit(); // Resets state
    this.isModalOpen = true;
    this.errorMessage = null;
    this.generalErrorMessage = null;
  }

  async saveArtist() {
    this.isSaving = true;
    this.errorMessage = null;
    this.generalErrorMessage = null;

    if (!this.newArtist.name || !this.newArtist.email) {
      this.generalErrorMessage = 'Please fill in all required fields (*).';
      this.isSaving = false;
      return;
    }

    try {
      if (this.editingId) {
        const dataToUpdate = {
          name: this.newArtist.name,
          email: this.newArtist.email,
          phone: this.newArtist.phone || '',
          address: this.newArtist.address || '',
          commission_amount: this.newArtist.commission_amount || null,
          bio: this.newArtist.bio || '',
          // KAN-149: was `isActive || true`, which made it impossible to deactivate an artist
          isActive: this.newArtist.isActive !== false
        };
        await this.artistService.updateArtist(this.editingId, dataToUpdate);
        this.editingId = null;
      } else {
        await this.artistService.addArtist(this.newArtist);
      }
      
      this.newArtist = { name: '', email: '', phone: '', address: '', commission_amount: null, bio: '', isActive: true };
      this.errorMessage = null;
      this.isModalOpen = false;
    } finally {
      this.isSaving = false;
    }
  }

  async editArtist(artist: any) {
    this.newArtist = { ...artist };
    this.editingId = artist.id;
    this.errorMessage = null;
    this.generalErrorMessage = null;
    this.deletingFile = null;
    this.uploadedFileUrl = null;
    this.uploadProgress = 0;
    this.isModalOpen = true;
    this.removeConfirmFrameId = null;
    this.rowError.clear();
    this.loadFrames();
  }

  cancelEdit() {
    this.newArtist = { name: '', email: '', phone: '', address: '', commission_amount: null, bio: '', isActive: true };
    this.editingId = null;
    this.errorMessage = null;
    this.generalErrorMessage = null;
    this.deletingFile = null;
    this.uploadedFileUrl = null;
    this.isModalOpen = false;
  }

  async deleteArtist(id: string) {
    if (confirm('Are you sure you want to delete this artist?')) {
      // Soft delete
      await this.artistService.deleteArtist(id);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscKey(event: Event) {
    // Escape closes only the top layer: the image preview closes itself, then the picker, then the artist modal.
    if (this.previewUrl) return;
    if (this.isPickerOpen) {
      if (!this.pickerSaving) this.closePicker();
      return;
    }
    if (this.isModalOpen) {
      this.cancelEdit();
    }
  }

  // --- Frame ownership (KAN-149) ---

  /** The artist being edited can receive frames only while active and not deleted. */
  get canAddFrames(): boolean {
    return !!this.editingId && isAssignableArtist({ id: this.editingId, isActive: this.newArtist.isActive, isDeleted: this.newArtist.isDeleted });
  }

  async loadFrames() {
    if (!this.editingId) return;
    this.framesLoading = true;
    this.framesError = null;
    try {
      const [catalog, ownerships] = await Promise.all([this.frameOwnership.getFrameCatalog(), this.frameOwnership.getAllOwnerships()]);
      this.frameCatalog = catalog;
      this.ownerships = ownerships;
      this.framesLoaded = true;
      this.refreshAssigned();
    } catch (e) {
      console.error('Error loading frames:', e);
      this.framesError = 'Frames could not be loaded. Close and reopen the artist to try again.';
    } finally {
      this.framesLoading = false;
    }
  }

  private refreshAssigned() {
    this.assignedFrames = this.editingId ? framesOwnedBy(this.frameCatalog, this.ownerships, this.editingId) : [];
    this.assignedFrames.forEach(e => this.loadThumb(e));
  }

  private loadThumb(entry: FrameCatalogEntry) {
    if (this.thumbUrls.has(entry.frameId)) return;
    this.thumbUrls.set(entry.frameId, null);
    // same image resolution as the Frame Report when the draft _FP path has no file
    this.frameOwnership.frameImageUrl(entry).then(url => this.thumbUrls.set(entry.frameId, url));
  }

  /** Hover text for a frame without a thumbnail: where the image was looked for. */
  noImageReason(frameId: string): string {
    if (!this.thumbUrls.has(frameId)) return 'Loading image…';
    return this.frameOwnership.imageDiagnostics.get(frameId) || 'Looking for the image…';
  }

  openFramePreview(entry: FrameCatalogEntry) {
    const url = this.thumbUrls.get(entry.frameId);
    if (url) this.previewUrl = url;
  }

  askRemove(frameId: string) {
    this.removeConfirmFrameId = frameId;
    this.rowError.delete(frameId);
  }

  /** Remove = back to Unassigned (company); the frame itself is never deleted. */
  async confirmRemove(entry: FrameCatalogEntry) {
    if (!this.editingId) return;
    this.removeConfirmFrameId = null;
    this.rowBusy.add(entry.frameId);
    try {
      await this.frameOwnership.setOwner(entry.frameId, null, entry.fpImagePath, this.editingId);
      this.ownerships.set(entry.frameId, { frameId: entry.frameId, artistId: null, fpImagePath: entry.fpImagePath });
      this.refreshAssigned();
    } catch (e: any) {
      this.rowError.set(entry.frameId, e?.message || 'The frame could not be removed.');
    } finally {
      this.rowBusy.delete(entry.frameId);
    }
  }

  openPicker() {
    if (!this.canAddFrames) return;
    this.isPickerOpen = true;
    this.pickerShowAll = false;
    this.pickerSearch = '';
    this.pickerScope = 'all';
    this.pickerSelected.clear();
    this.pickerConfirming = false;
    this.pickerMessage = null;
    this.pickerError = null;
    this.recomputePicker();
  }

  closePicker() {
    this.isPickerOpen = false;
    this.pickerConfirming = false;
  }

  recomputePicker() {
    if (!this.editingId) return;
    this.pickerRows = filterPickerFrames(this.frameCatalog, this.ownerships, this.artistsById, this.editingId, {
      showAll: this.pickerShowAll,
      search: this.pickerSearch,
      scope: this.pickerScope
    });
    this.pickerPage = 0;
    this.pagedPickerRows.forEach(r => this.loadThumb(r.entry));
  }

  get pickerPageCount(): number {
    return Math.max(1, Math.ceil(this.pickerRows.length / PICKER_PAGE_SIZE));
  }

  get pagedPickerRows(): PickerRow[] {
    return this.pickerRows.slice(this.pickerPage * PICKER_PAGE_SIZE, (this.pickerPage + 1) * PICKER_PAGE_SIZE);
  }

  goPickerPage(delta: number) {
    this.pickerPage = Math.min(this.pickerPageCount - 1, Math.max(0, this.pickerPage + delta));
    this.pagedPickerRows.forEach(r => this.loadThumb(r.entry));
  }

  togglePick(frameId: string) {
    if (this.pickerSelected.has(frameId)) this.pickerSelected.delete(frameId);
    else this.pickerSelected.add(frameId);
    this.pickerConfirming = false;
  }

  /** Selected rows that belong to another live artist: these need the take confirm. */
  get rowsToTake(): PickerRow[] {
    return this.pickerRows.filter(r => this.pickerSelected.has(r.entry.frameId) && needsTakeConfirm(r.owner));
  }

  async addSelectedFrames() {
    if (!this.editingId || this.pickerSelected.size === 0) return;
    if (this.rowsToTake.length > 0 && !this.pickerConfirming) {
      this.pickerConfirming = true;
      return;
    }
    const artistId = this.editingId;
    const rows = this.pickerRows.filter(r => this.pickerSelected.has(r.entry.frameId));
    this.pickerSaving = true;
    this.pickerError = null;
    this.pickerMessage = null;
    let failed = 0;
    for (const row of rows) {
      try {
        await this.frameOwnership.setOwner(row.entry.frameId, artistId, row.entry.fpImagePath, row.owner.artistId);
        this.ownerships.set(row.entry.frameId, { frameId: row.entry.frameId, artistId, fpImagePath: row.entry.fpImagePath });
        this.pickerSelected.delete(row.entry.frameId);
      } catch (e: any) {
        failed++;
        this.rowError.set(row.entry.frameId, e?.message || 'Could not be updated.');
      }
    }
    this.pickerSaving = false;
    this.pickerConfirming = false;
    this.refreshAssigned();
    if (failed === 0) {
      this.closePicker();
      return;
    }
    this.pickerError = `${failed} of ${rows.length} frames could not be updated. They stay selected.`;
    this.recomputePicker();
  }

  // --- File Upload Logic ---

  getFilesByType(type: string) {
    if (!this.newArtist || !this.newArtist.files) return [];
    return this.newArtist.files.filter((f: any) => (f.type || 'Other') === type);
  }

  getDocsTooltip(artist: any): string {
    if (!artist || !artist.files || artist.files.length === 0) return '';
    const types = artist.files.map((f: any) => f.type || 'Other');
    const uniqueTypes = [...new Set(types)];
    return 'Uploaded: ' + uniqueTypes.join(', ');
  }

  async uploadSpecificFile(event: any, docType: string) {
    const files = event.target.files;
    if (!this.editingId || !files || files.length === 0) return;

    this.uploadingFile = true;
    this.uploadProgress = 0;

    const companyId = this.editingId;
    const currentFiles = this.newArtist.files || [];

    try {
      const uploadPromises = Array.from(files).map((file: any) => {
        return this.artistService.uploadFile(companyId, file, (progress) => {
          this.uploadProgress = progress;
        }).then(fileData => {
          currentFiles.push({ ...fileData, type: docType });
        });
      });

      await Promise.all(uploadPromises);
      await this.artistService.updateArtistFiles(companyId, currentFiles);

      this.newArtist.files = currentFiles;
      
      const artistInList = this.artists.find(a => a.id === companyId);
      if (artistInList) {
        artistInList.files = currentFiles;
      }

      this.uploadingFile = false;
      this.uploadedFileUrl = 'done';
      
      event.target.value = '';
      
      setTimeout(() => {
        if (this.uploadedFileUrl === 'done') this.uploadedFileUrl = null;
      }, 3000);
      
    } catch (error) {
      console.error('Error starting upload:', error);
      this.uploadingFile = false;
      alert('An error occurred during the upload.');
    }
  }

  async deleteSpecificFile(file: any) {
    if (!this.newArtist || !this.newArtist.files) return;
    if (!confirm('Are you sure you want to delete this attached document?')) return;
    
    const index = this.newArtist.files.indexOf(file);
    if (index === -1) return;
    
    this.deletingFile = file;
    
    try {
      if (file.storagePath) {
        await this.artistService.deleteFile(file.storagePath);
      }
      
      this.newArtist.files.splice(index, 1);
      await this.artistService.updateArtistFiles(this.editingId!, this.newArtist.files);
      
      const artistInList = this.artists.find(a => a.id === this.editingId);
      if (artistInList) {
        artistInList.files = this.newArtist.files;
      }
      
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete document.');
    } finally {
      this.deletingFile = null;
    }
  }

  // --- Sorting ---
  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortArtists();
  }

  private sortArtists() {
    if (!this.artists || this.artists.length === 0) {
      return;
    }

    this.artists.sort((a, b) => {
      const valA = a[this.sortColumn];
      const valB = b[this.sortColumn];

      let comparison = 0;
      
      if (valA == null && valB == null) {
        comparison = 0;
      } else if (valA == null) {
        comparison = -1;
      } else if (valB == null) {
        comparison = 1;
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        comparison = valA - valB;
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        comparison = valA === valB ? 0 : valA ? 1 : -1;
      } else {
        comparison = String(valA).localeCompare(String(valB));
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }
}
